import {Service} from '../service/service.js';
import {pageRegistry} from './pageRegistry.js';

var SAVE_DEBOUNCE_MS = 400;
var RESIZE_DEBOUNCE_MS = 700;

/**
 * Pack, apply, and bind save/restore of primary grid column order, visibility, width, and wrap.
 */
export var GridColumnLayoutUtil = {

	isSaveDataLayoutEnabled: function() {
		var user = window.DL && window.DL.currentUser;
		return !!(user && user.userProfile && user.userProfile.saveDataLayout === true);
	},

	resolvePageKey: function(grid) {
		if (pageRegistry && pageRegistry.activePageKey) {
			return pageRegistry.activePageKey;
		}
		if (grid) {
			var node = grid;
			while (node) {
				if (node.getAttribute && node.getAttribute('data-page-key')) {
					return node.getAttribute('data-page-key');
				}
				node = node.parentElement || (node.getRootNode && node.getRootNode().host) || null;
			}
		}
		return null;
	},

	/**
	 * Resolve a stable grid id from common element identifiers.
	 * Prefers explicit grid id attributes, then itemId/itemid (project convention).
	 */
	resolveGridId: function(element, options) {
		options = options || {};
		if (options.gridId) {
			return options.gridId;
		}
		if (!element) {
			return 'primary';
		}
		return element.gridId
			|| (element.getAttribute && (
				element.getAttribute('grid-id')
				|| element.getAttribute('gridId')
				|| element.getAttribute('itemId')
				|| element.getAttribute('itemid')
			))
			|| element.itemId
			|| 'primary';
	},

	getThemeTokens: function(grid) {
		if (!grid) {
			return [];
		}
		var theme = grid.getAttribute ? (grid.getAttribute('theme') || '') : (grid.theme || '');
		return String(theme).split(/\s+/).filter(function(token) {
			return !!token;
		});
	},

	isWrapTextEnabled: function(grid) {
		return GridColumnLayoutUtil.getThemeTokens(grid).indexOf('wrap-cell-content') !== -1;
	},

	/**
	 * Apply wrap preference to the grid theme. Null/undefined leaves the coded default.
	 */
	applyWrapText: function(grid, wrapText) {
		if (!grid || wrapText === null || wrapText === undefined) {
			return;
		}
		var tokens = GridColumnLayoutUtil.getThemeTokens(grid).filter(function(token) {
			return token !== 'wrap-cell-content';
		});
		if (wrapText === true) {
			tokens.push('wrap-cell-content');
		}
		var theme = tokens.join(' ');
		if (grid.setAttribute) {
			grid.setAttribute('theme', theme);
		}
		grid.theme = theme;
	},

	getPathColumns: function(grid) {
		var columns = [];
		if (!grid) {
			return columns;
		}
		Array.from(grid.childNodes).forEach(function(child) {
			if (!child || !child.tagName) {
				return;
			}
			var tagName = child.tagName.toUpperCase();
			if (tagName.indexOf('VAADIN-GRID') === 0 && tagName.indexOf('COLUMN') !== -1) {
				var path = child.getAttribute('path') || child.path;
				if (path) {
					columns.push(child);
				}
			}
		});
		return columns;
	},

	/**
	 * Vaadin reorders via column._order (CSS/visual), not light-DOM child order.
	 * Sort path columns by _order so pack/apply match what the user sees.
	 */
	getPathColumnsInVisualOrder: function(grid) {
		var columns = GridColumnLayoutUtil.getPathColumns(grid);
		columns.sort(function(left, right) {
			var leftOrder = left._order != null ? left._order : 0;
			var rightOrder = right._order != null ? right._order : 0;
			return leftOrder - rightOrder;
		});
		return columns;
	},

	packColumns: function(grid) {
		var packed = [];
		GridColumnLayoutUtil.getPathColumnsInVisualOrder(grid).forEach(function(column) {
			var path = column.getAttribute('path') || column.path;
			var width = column.width || column.getAttribute('width') || null;
			packed.push({
				path: path,
				visible: column.hidden !== true,
				width: width
			});
		});
		return packed;
	},

	/**
	 * Restore known columns first (order/visible/width), then append unknown columns
	 * at the end using their coded defaults. Missing saved paths are ignored.
	 */
	applyColumns: function(grid, columns) {
		if (!grid || !columns || !Array.isArray(columns) || columns.length === 0) {
			return;
		}

		var pathColumns = GridColumnLayoutUtil.getPathColumns(grid);
		if (pathColumns.length === 0) {
			return;
		}

		var columnByPath = {};
		pathColumns.forEach(function(column) {
			var path = column.getAttribute('path') || column.path;
			columnByPath[path] = column;
		});

		var restored = [];
		var restoredPaths = {};
		columns.forEach(function(saved) {
			if (!saved || !saved.path) {
				return;
			}
			var column = columnByPath[saved.path];
			if (!column) {
				return;
			}
			column.hidden = saved.visible === false;
			if (saved.width) {
				column.width = saved.width;
				column.setAttribute('width', saved.width);
			}
			restored.push(column);
			restoredPaths[saved.path] = true;
		});

		var unknowns = [];
		pathColumns.forEach(function(column) {
			var path = column.getAttribute('path') || column.path;
			if (!restoredPaths[path]) {
				unknowns.push(column);
			}
		});

		// Reorder light DOM, then reset Vaadin visual _order to match DOM.
		restored.forEach(function(column) {
			grid.appendChild(column);
		});
		unknowns.forEach(function(column) {
			grid.appendChild(column);
		});
		if (typeof grid._resetColumnOrder === 'function') {
			grid._resetColumnOrder();
		}
	},

	scheduleSave: function(binding, debounceMs) {
		if (!binding || binding.applyingLayout) {
			return;
		}
		if (!GridColumnLayoutUtil.isSaveDataLayoutEnabled()) {
			return;
		}
		if (binding.saveTimer) {
			clearTimeout(binding.saveTimer);
		}
		binding.saveTimer = setTimeout(function() {
			binding.saveTimer = null;
			GridColumnLayoutUtil.saveNow(binding);
		}, debounceMs || SAVE_DEBOUNCE_MS);
	},

	saveNow: function(binding) {
		if (!binding || !binding.grid || binding.applyingLayout) {
			return;
		}
		if (!GridColumnLayoutUtil.isSaveDataLayoutEnabled()) {
			return;
		}
		var columns = GridColumnLayoutUtil.packColumns(binding.grid);
		if (!columns.length) {
			return;
		}
		var pageKey = binding.pageKey || GridColumnLayoutUtil.resolvePageKey(binding.grid);
		if (!pageKey || !binding.gridId) {
			return;
		}
		Service.getGridColumnLayoutService().saveLayout({
			pageKey: pageKey,
			gridId: binding.gridId,
			columns: columns,
			wrapText: GridColumnLayoutUtil.isWrapTextEnabled(binding.grid)
		}).catch(function() {
			// Keep UI usable if save fails; next change will retry.
		});
	},

	notifyColumnVisibilityChanged: function(grid) {
		if (!grid || !grid.__dlColumnLayoutBinding) {
			return;
		}
		GridColumnLayoutUtil.scheduleSave(grid.__dlColumnLayoutBinding, SAVE_DEBOUNCE_MS);
	},

	notifyWrapTextChanged: function(grid) {
		if (!grid || !grid.__dlColumnLayoutBinding) {
			return;
		}
		GridColumnLayoutUtil.scheduleSave(grid.__dlColumnLayoutBinding, SAVE_DEBOUNCE_MS);
	},

	/**
	 * Bind save/restore for a primary grid. No-op when Save data layout is Off.
	 * @param {HTMLElement} grid
	 * @param {{pageKey?: string, gridId?: string}} options
	 */
	bindPrimaryGrid: function(grid, options) {
		if (!grid) {
			return;
		}
		options = options || {};
		var gridId = GridColumnLayoutUtil.resolveGridId(grid, options);
		var pageKey = options.pageKey || GridColumnLayoutUtil.resolvePageKey(grid);

		if (grid.__dlColumnLayoutBinding) {
			grid.__dlColumnLayoutBinding.gridId = gridId;
			grid.__dlColumnLayoutBinding.pageKey = pageKey;
			if (GridColumnLayoutUtil.isSaveDataLayoutEnabled() && !grid.__dlColumnLayoutBinding.applied) {
				GridColumnLayoutUtil.restoreLayout(grid.__dlColumnLayoutBinding);
			}
			return grid.__dlColumnLayoutBinding;
		}

		var binding = {
			grid: grid,
			gridId: gridId,
			pageKey: pageKey,
			applyingLayout: false,
			saveTimer: null,
			applied: false
		};
		grid.__dlColumnLayoutBinding = binding;

		var onReorder = function() {
			GridColumnLayoutUtil.scheduleSave(binding, SAVE_DEBOUNCE_MS);
		};
		var onResize = function() {
			GridColumnLayoutUtil.scheduleSave(binding, RESIZE_DEBOUNCE_MS);
		};

		grid.addEventListener('column-reorder', onReorder);
		grid.addEventListener('column-reorder-end', onReorder);
		grid.addEventListener('column-resize', onResize);

		binding._onReorder = onReorder;
		binding._onResize = onResize;

		if (GridColumnLayoutUtil.isSaveDataLayoutEnabled()) {
			GridColumnLayoutUtil.restoreLayout(binding);
		}

		return binding;
	},

	restoreLayout: function(binding) {
		if (!binding || !binding.grid || binding.applied) {
			return;
		}
		if (!GridColumnLayoutUtil.isSaveDataLayoutEnabled()) {
			return;
		}
		var pageKey = binding.pageKey || GridColumnLayoutUtil.resolvePageKey(binding.grid);
		if (!pageKey) {
			return;
		}
		binding.pageKey = pageKey;

		Service.getGridColumnLayoutService().getLayoutsForPage(pageKey).then(function(layouts) {
			if (!layouts || !Array.isArray(layouts)) {
				return;
			}
			var match = null;
			layouts.forEach(function(layout) {
				if (layout && layout.gridId === binding.gridId) {
					match = layout;
				}
			});
			if (!match || !match.columns || binding.applied) {
				return;
			}
			binding.applyingLayout = true;
			try {
				GridColumnLayoutUtil.applyColumns(binding.grid, match.columns);
				GridColumnLayoutUtil.applyWrapText(binding.grid, match.wrapText);
				binding.applied = true;
				binding.grid.dispatchEvent(new CustomEvent('dl-column-layout-applied'));
			} finally {
				// Allow Vaadin to finish DOM updates before saves are allowed again.
				setTimeout(function() {
					binding.applyingLayout = false;
				}, 200);
			}
		}).catch(function() {
			// Leave coded defaults if restore fails.
		});
	}
};
